import {isMap, type PropertyMap} from "../../common/utils/objects";
import {GLOBAL} from "../../common/globals";
import type {Maybe} from "../../bootstrap/common/types";
import {EopErrorHandler} from "../../common/errorHandler";
import {resolve} from "../../container";

export type DictionaryElement = HTMLElement & {
    _dictionary?: Dictionary;
};

export class Dictionary {

    private parent: Maybe<Dictionary>;
    private messages: Map<string, string | Map<string, string>>;

    public constructor(parent?: Dictionary) {
        this.parent = parent;
        this.messages = new Map();
    }

    public static root(): Dictionary {
        return rootDictionary;
    }

    public static ofBody(): Dictionary {
        return Dictionary.of(GLOBAL.bodyElement());
    }

    public static of(element: Element): Dictionary {
        const dictionaryElement = element.closestThat(elm => !!Dictionary.fetch(elm));
        return dictionaryElement
            ? Dictionary.extractDictionary(dictionaryElement)!
            : Dictionary.root();
    }

    private static fetch(element: Element): Maybe<Dictionary> {
        let dictionary = Dictionary.extractDictionary(element);
        if (!dictionary) {
            const entries = Array.from(element.children).filter(it => "eop-dictionary-entry" === it.tagName.toLowerCase());
            if (entries.length > 0) {
                const parentDictionary = element.parentElement ? Dictionary.of(element.parentElement) : undefined;
                dictionary = new Dictionary(parentDictionary).attachTo(element);
                entries.forEach(entry => dictionary!.addMessageFromEntry(entry));
            }
        }
        return dictionary;
    }

    private static extractDictionary(element: Element): Maybe<Dictionary> {
        return (element as DictionaryElement)._dictionary;
    }

    private attachTo(element: Element): this {
        (element as DictionaryElement)._dictionary = this;
        return this;
    }

    private addMessageFromEntry(entry: Element): void {
        const key = entry.getAttribute("key");
        const subKey = entry.getAttribute("sub-key");
        if (key) {
            this.addMessage(key, entry.innerHTML, subKey ?? undefined);
        }
    }

    public addMessage(key: string, value: string, subKey?: string): void {
        if (subKey) {
            let entry = this.messages.get(key);
            if (!isMap(entry)) {
                entry = new Map();
                this.messages.set(key, entry);
            }
            entry.set(subKey, value);
        } else {
            this.messages.set(key, value);
        }
    }

    public getMessage(key?: string, subkey: string = "_"): Maybe<string> {
        const value = key ? this.messages.get(key) : undefined;
        if (!value) {
            return this.parent?.getMessage(key, subkey);
        } else if (isMap(value)) {
            return value.get(subkey);
        } else {
            return value;
        }
    }

    public translate(key: string, subkey?: string, values?: PropertyMap): string {
        let translated = this.getMessage(key, subkey) ?? ("#" + key + "#");
        if (values) {
            for (const k in values) {
                const value = values[k];
                translated = translated.replace(k, value);
            }
        }
        return translated;
    }
}

const rootDictionary = new Dictionary();

/**
 * @warning might not work in safari
 */
export class EopMsg extends HTMLElement {

    public constructor(private errorHandler: EopErrorHandler = resolve(EopErrorHandler)) {
        super();
    }

    private dictionary: Dictionary;

    public connectedCallback(): void {
        this.dictionary = Dictionary.of(this.parentElement!);

        const key = this.getAttribute("key") ?? undefined;
        const subKey = this.getAttribute("sub-key") ?? undefined;
        const value = this.dictionary.getMessage(key, subKey);
        if (!value) {
            this.errorHandler.report(`dictionary key <${key}> missing.`);
        }
        this.outerHTML = value ?? ("#" + key + "#");
    }
}

customElements.define("eop-msg", EopMsg);